import React, { useEffect, useState } from 'react';
import { CTAButton } from './CTAButton';
import { ShieldCheckIcon, ClockIcon, WrenchIcon, FlameIcon, ShieldIcon, CheckIcon, ArrowRightIcon, ChevronDownIcon, ChevronUpIcon, AlertTriangleIcon, HeartIcon, HomeIcon, UserIcon, BadgeCheckIcon } from 'lucide-react';
interface QuizResultProps {
  score: number;
}
export function QuizResult({
  score
}: QuizResultProps) {
  const [activeFAQ, setActiveFAQ] = useState<number | null>(null);
  const [peopleCount] = useState(Math.floor(Math.random() * 20) + 120); // Random number between 120-140
  // Determine risk level and content based on score
  let resultText = '';
  let resultDescription = '';
  let riskLevel = '';
  let riskColor = '';
  let riskPercentage = 0;
  if (score <= 3) {
    resultText = 'Låg risk – stark grund';
    resultDescription = 'Bra grundskydd! Ett professionellt övervakat larm kan ändå ge snabbare åtgärd vid brand/inbrott och sänka stressnivån. Se om det skulle passa dig – utan bindning.';
    riskLevel = 'Låg';
    riskColor = 'green';
    riskPercentage = 33;
  } else if (score <= 7) {
    resultText = 'Medelrisk – tydliga förbättringar möjliga';
    resultDescription = 'Du har delar på plats, men viktiga luckor finns (t.ex. övervakning, brand, skalskydd). Få en kostnadsfri genomgång av hemmet för att se om vårt larm passar dina behov.';
    riskLevel = 'Medel';
    riskColor = 'orange';
    riskPercentage = 66;
  } else {
    resultText = 'Hög risk – åtgärda snarast';
    resultDescription = 'Flera sårbarheter gör hemmet mer utsatt. Med Verisure kan larmcentralen agera inom sekunder, även när du sover eller är bortrest. Ta 30 sekunder till och se om vårt larm passar ditt hem.';
    riskLevel = 'Hög';
    riskColor = 'red';
    riskPercentage = 95;
  }
  const handleCTAClick = () => {
    window.location.href = 'https://go.adt212.net/t/t?a=1960533651&as=1971362379&t=2&tk=1';
  };
  const toggleFAQ = (index: number) => {
    setActiveFAQ(activeFAQ === index ? null : index);
  };
  return <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100" data-id="element-221">
      {/* Non-sticky completion message */}
      <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-8 text-center" data-id="element-222">
        <div className="text-xl font-bold text-red-600" data-id="element-223">
          Din analys är klar!
        </div>
        <p className="text-sm text-red-700 mt-1" data-id="element-224">
          Nedan ser du din personliga säkerhetsanalys
        </p>
      </div>

      {/* Header with personalized result */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-6 -mx-6 -mt-6 mb-8 rounded-t-lg" data-id="element-225">
        <h2 className="text-2xl font-bold text-center mb-4" data-id="element-226">
          Din säkerhetsanalys
        </h2>
        <div className="max-w-md mx-auto" data-id="element-227">
          <div className="flex items-center justify-between mb-2" data-id="element-228">
            <span className="text-sm" data-id="element-229">Låg risk</span>
            <span className="text-sm" data-id="element-230">Hög risk</span>
          </div>
          <div className="h-6 bg-gray-700 rounded-full overflow-hidden mb-3" data-id="element-231">
            <div className={`h-full ${riskColor === 'green' ? 'bg-green-500' : riskColor === 'orange' ? 'bg-orange-500' : 'bg-red-600'} transition-all duration-1000 ease-out`} style={{
            width: `${riskPercentage}%`
          }} data-id="element-232"></div>
          </div>
          <div className="flex justify-center mb-4" data-id="element-233">
            <div className={`inline-flex items-center px-4 py-1 rounded-full text-white font-medium ${riskColor === 'green' ? 'bg-green-600' : riskColor === 'orange' ? 'bg-orange-600' : 'bg-red-600'}`} data-id="element-234">
              {riskColor === 'red' && <AlertTriangleIcon className="h-4 w-4 mr-1" data-id="element-235" />}
              {riskLevel} risknivå: {riskPercentage}%
            </div>
          </div>
          <h3 className="text-xl font-bold mb-3 text-center" data-id="element-236">{resultText}</h3>
          <p className="text-sm text-gray-300 text-center mb-4" data-id="element-237">
            {resultDescription}
          </p>
          <div className="text-center" data-id="element-238">
            <CTAButton onClick={handleCTAClick} text="Se om Verisure passar ditt hem" primary size="large" icon={<ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-240" />} href="https://go.adt212.net/t/t?a=1960533651&as=1971362379&t=2&tk=1" className="shadow-xl hover:shadow-2xl bg-red-600 hover:bg-red-700 transform hover:scale-105 transition-all" fullWidth data-id="element-239" />
          </div>
          <div className="flex justify-center mt-4" data-id="element-241">
            <div className="flex items-center text-sm text-gray-300 space-x-4" data-id="element-242">
              <div className="flex items-center" data-id="element-243">
                <BadgeCheckIcon className="h-4 w-4 mr-1" data-id="element-244" />
                <span data-id="element-245">Gratis</span>
              </div>
              <div className="flex items-center" data-id="element-246">
                <ClockIcon className="h-4 w-4 mr-1" data-id="element-247" />
                <span data-id="element-248">Tar 30 sek</span>
              </div>
              <div className="flex items-center" data-id="element-249">
                <UserIcon className="h-4 w-4 mr-1" data-id="element-250" />
                <span data-id="element-251">Ingen bindning</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social proof counter - without time limit */}
      <div className="bg-gray-50 p-4 rounded-lg mb-8 text-center" data-id="element-252">
        <div className="flex items-center justify-center text-sm" data-id="element-253">
          <div className="flex -space-x-2 mr-3" data-id="element-254">
            <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center overflow-hidden" data-id="element-255">
              <UserIcon className="h-3 w-3 text-gray-600" data-id="element-256" />
            </div>
            <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center overflow-hidden" data-id="element-257">
              <UserIcon className="h-3 w-3 text-gray-600" data-id="element-258" />
            </div>
            <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center overflow-hidden" data-id="element-259">
              <UserIcon className="h-3 w-3 text-gray-600" data-id="element-260" />
            </div>
          </div>
          <span data-id="element-261">
            <strong data-id="element-262">{peopleCount}</strong> personer har fått sin säkerhetsanalys
            idag
          </span>
        </div>
      </div>

      {/* Main content area */}
      <div className="space-y-8" data-id="element-263">
        {/* Testimonial highlight */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg" data-id="element-264">
          <div className="flex text-yellow-400 mb-2" data-id="element-265">★★★★★</div>
          <p className="italic mb-2" data-id="element-266">
            "Jag hade ingen aning om hur sårbart mitt hem var förrän jag tog
            quizet. Verisures rådgivare hittade flera svaga punkter jag aldrig
            tänkt på. Nu kan jag äntligen sova lugnt."
          </p>
          <p className="font-medium" data-id="element-267">Maria L, Stockholm</p>
        </div>

        {/* Comparison Table Section - Enhanced */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200" data-id="element-268">
          <h4 className="text-xl font-semibold text-center mb-6" data-id="element-269">Jämförelse</h4>
          <div className="overflow-x-auto" data-id="element-270">
            <table className="w-full border-collapse" data-id="element-271">
              <thead data-id="element-272">
                <tr data-id="element-273">
                  <th className="text-left p-3 w-1/3" data-id="element-274"></th>
                  <th className="p-3 bg-gray-100 text-center rounded-t-lg" data-id="element-275">
                    Eget larm
                  </th>
                  <th className="p-3 bg-red-100 text-center text-red-800 rounded-t-lg" data-id="element-276">
                    Professionellt övervakat larm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-277">
                <tr className="border-t border-gray-200" data-id="element-278">
                  <td className="p-3 font-medium" data-id="element-279">Övervakning</td>
                  <td className="p-3 bg-gray-100 text-center" data-id="element-280">Manuellt</td>
                  <td className="p-3 bg-red-50 text-center" data-id="element-281">
                    <span className="font-medium" data-id="element-282">Operatörer dygnet runt</span>
                  </td>
                </tr>
                <tr className="border-t border-gray-200" data-id="element-283">
                  <td className="p-3 font-medium" data-id="element-284">Åtgärd</td>
                  <td className="p-3 bg-gray-100 text-center" data-id="element-285">
                    När du ser det
                  </td>
                  <td className="p-3 bg-red-50 text-center" data-id="element-286">
                    <span className="font-medium" data-id="element-287">Agerar inom sekunder</span>
                  </td>
                </tr>
                <tr className="border-t border-gray-200" data-id="element-288">
                  <td className="p-3 font-medium" data-id="element-289">Vid brand</td>
                  <td className="p-3 bg-gray-100 text-center" data-id="element-290">Sirén</td>
                  <td className="p-3 bg-red-50 text-center" data-id="element-291">
                    <span className="font-medium" data-id="element-292">
                      Operatörer kontaktar räddningstjänst
                    </span>
                  </td>
                </tr>
                <tr className="border-t border-gray-200" data-id="element-293">
                  <td className="p-3 font-medium" data-id="element-294">Vid inbrott</td>
                  <td className="p-3 bg-gray-100 text-center" data-id="element-295">Signal</td>
                  <td className="p-3 bg-red-50 text-center" data-id="element-296">
                    <span className="font-medium" data-id="element-297">
                      Rök/åtgärd på distans + väktare
                    </span>
                  </td>
                </tr>
                <tr className="border-t border-gray-200" data-id="element-298">
                  <td className="p-3 font-medium" data-id="element-299">Trygghet borta/sömn</td>
                  <td className="p-3 bg-gray-100 text-center rounded-b-lg" data-id="element-300">
                    Begränsad
                  </td>
                  <td className="p-3 bg-red-50 text-center rounded-b-lg" data-id="element-301">
                    <span className="font-medium" data-id="element-302">Hög</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-center mt-6" data-id="element-303">
            <CTAButton onClick={handleCTAClick} text="Få mer information om säkerhetslösningar" primary href="https://go.adt212.net/t/t?a=1960533651&as=1971362379&t=2&tk=1" className="shadow-md hover:shadow-lg" data-id="element-304" />
          </div>
        </div>

        {/* Trust Signals Section - Enhanced */}
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200" data-id="element-305">
          <h4 className="font-semibold mb-4" data-id="element-306">Varför välja Verisure?</h4>
          <ul className="space-y-4 mb-6" data-id="element-307">
            <li className="flex items-start" data-id="element-308">
              <span className="text-red-600 mr-2 flex-shrink-0 mt-1" data-id="element-309">
                <CheckIcon className="h-5 w-5" data-id="element-310" />
              </span>
              <span data-id="element-311">
                <strong data-id="element-312">Sveriges största larmcentral</strong> – operatörer som
                agerar inom sekunder, dygnet runt.
              </span>
            </li>
            <li className="flex items-start" data-id="element-313">
              <span className="text-red-600 mr-2 flex-shrink-0 mt-1" data-id="element-314">
                <CheckIcon className="h-5 w-5" data-id="element-315" />
              </span>
              <span data-id="element-316">
                <strong data-id="element-317">35+ års erfarenhet</strong>, 17 länder i Europa &
                Latinamerika, 5,1 miljoner kunder världen över.
              </span>
            </li>
            <li className="flex items-start" data-id="element-318">
              <span className="text-red-600 mr-2 flex-shrink-0 mt-1" data-id="element-319">
                <CheckIcon className="h-5 w-5" data-id="element-320" />
              </span>
              <span data-id="element-321">
                <strong data-id="element-322">Inbrottslarm</strong> som kan larma redan innan någon
                tagit sig in.
              </span>
            </li>
            <li className="flex items-start" data-id="element-323">
              <span className="text-red-600 mr-2 flex-shrink-0 mt-1" data-id="element-324">
                <CheckIcon className="h-5 w-5" data-id="element-325" />
              </span>
              <span data-id="element-326">
                <strong data-id="element-327">Brandlarm</strong> som alltid är aktivt – även när du
                sover eller är bortrest.
              </span>
            </li>
            <li className="flex items-start" data-id="element-328">
              <span className="text-red-600 mr-2 flex-shrink-0 mt-1" data-id="element-329">
                <CheckIcon className="h-5 w-5" data-id="element-330" />
              </span>
              <span data-id="element-331">
                <strong data-id="element-332">Kontaktlarm</strong> med direktkoppling till larmcentral
                vid kritiska situationer.
              </span>
            </li>
          </ul>
          <div className="flex justify-around flex-wrap gap-4 text-center" data-id="element-333">
            <div className="flex flex-col items-center" data-id="element-334">
              <ShieldCheckIcon className="h-8 w-8 text-red-600 mb-2" data-id="element-335" />
              <span className="text-sm font-semibold" data-id="element-336">
                Trygghet dygnet runt
              </span>
            </div>
            <div className="flex flex-col items-center" data-id="element-337">
              <WrenchIcon className="h-8 w-8 text-red-600 mb-2" data-id="element-338" />
              <span className="text-sm font-semibold" data-id="element-339">Installationshjälp</span>
            </div>
            <div className="flex flex-col items-center" data-id="element-340">
              <ClockIcon className="h-8 w-8 text-red-600 mb-2" data-id="element-341" />
              <span className="text-sm font-semibold" data-id="element-342">Service ingår</span>
            </div>
          </div>
        </div>

        {/* Fact Boxes Section - Enhanced */}
        <div data-id="element-343">
          <h4 className="font-semibold mb-4 text-center" data-id="element-344">
            Varför ett övervakat larm?
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-345">
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-346">
              <div className="flex justify-center mb-4" data-id="element-347">
                <ClockIcon className="h-12 w-12 text-red-600" data-id="element-348" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2" data-id="element-349">
                Snabb åtgärd dygnet runt
              </h3>
              <p className="text-sm mb-2" data-id="element-350">
                Operatörer kan agera inom sekunder och t.ex. aktivera rök
                avskräckande på distans.
              </p>
              <p className="text-xs text-gray-500 text-center" data-id="element-351">
                Källa: Verisure
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-352">
              <div className="flex justify-center mb-4" data-id="element-353">
                <FlameIcon className="h-12 w-12 text-red-600" data-id="element-354" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2" data-id="element-355">
                Brand: tid är allt
              </h3>
              <p className="text-sm mb-2" data-id="element-356">
                Tidigt larm och åtgärd kan vara avgörande när rök utvecklas
                snabbt.
              </p>
              <p className="text-xs text-gray-500 text-center" data-id="element-357">Källa: MSB</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 hover:shadow-lg transition-shadow" data-id="element-358">
              <div className="flex justify-center mb-4" data-id="element-359">
                <ShieldIcon className="h-12 w-12 text-red-600" data-id="element-360" />
              </div>
              <h3 className="text-lg font-semibold text-center mb-2" data-id="element-361">
                Förebyggande effekt
              </h3>
              <p className="text-sm mb-2" data-id="element-362">
                Synligt larm, skyltning och kamera kan minska risken för försök.
              </p>
              <p className="text-xs text-gray-500 text-center" data-id="element-363">
                Källa: SSF/Brå
              </p>
            </div>
          </div>
        </div>

        {/* Social Proof Section - Enhanced */}
        <div className="bg-gray-50 p-6 rounded-lg" data-id="element-364">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12 mb-6" data-id="element-365">
            <div className="text-center" data-id="element-366">
              <div className="text-xl font-bold text-red-600" data-id="element-367">35+</div>
              <div className="text-sm" data-id="element-368">års erfarenhet</div>
            </div>
            <div className="text-center" data-id="element-369">
              <div className="text-xl font-bold text-red-600" data-id="element-370">17</div>
              <div className="text-sm" data-id="element-371">länder i Europa & Latinamerika</div>
            </div>
            <div className="text-center" data-id="element-372">
              <div className="text-xl font-bold text-red-600" data-id="element-373">5,1 miljoner</div>
              <div className="text-sm" data-id="element-374">kunder världen över</div>
            </div>
            <div className="text-center" data-id="element-375">
              <div className="text-xl font-bold text-red-600" data-id="element-376">#1</div>
              <div className="text-sm" data-id="element-377">Sveriges största larmcentral</div>
            </div>
          </div>
          {/* Enhanced testimonials with photos */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-378">
            <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" data-id="element-379">
              <div className="flex items-start gap-3" data-id="element-380">
                <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Anders" className="w-12 h-12 rounded-full object-cover" data-id="element-381" />
                <div data-id="element-382">
                  <div className="flex text-yellow-400 mb-1" data-id="element-383">★★★★★</div>
                  <p className="italic mb-2 text-sm" data-id="element-384">
                    "Vi kände oss aldrig helt trygga i vårt hus förrän vi
                    installerade Verisure. Nu kan vi äntligen slappna av."
                  </p>
                  <p className="text-sm font-medium" data-id="element-385">Anders och Maria L.</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200" data-id="element-386">
              <div className="flex items-start gap-3" data-id="element-387">
                <img src="https://randomuser.me/api/portraits/women/44.jpg" alt="Karin" className="w-12 h-12 rounded-full object-cover" data-id="element-388" />
                <div data-id="element-389">
                  <div className="flex text-yellow-400 mb-1" data-id="element-390">★★★★★</div>
                  <p className="italic mb-2 text-sm" data-id="element-391">
                    "När grannen hade inbrott förstod vi hur sårbart vårt hem
                    var. Verisure ger oss trygghet både hemma och när vi är
                    borta."
                  </p>
                  <p className="text-sm font-medium" data-id="element-392">Karin J.</p>
                </div>
              </div>
            </div>
          </div>
          <p className="text-center italic mt-6 font-medium" data-id="element-393">
            "Vi skyddar det som betyder mest."
          </p>
        </div>

        {/* FAQ Section - Enhanced */}
        <div data-id="element-394">
          <h4 className="font-semibold mb-6 text-center" data-id="element-395">Vanliga frågor</h4>
          <div className="space-y-4" data-id="element-396">
            {[{
            question: 'Kostar det något att få information?',
            answer: 'Nej, det är helt kostnadsfritt och utan bindning. En rådgivare går igenom dina behov och ger ett personligt förslag.'
          }, {
            question: 'Måste jag bestämma mig idag?',
            answer: 'Nej, absolut inte. En rådgivare ringer och går igenom dina behov i lugn och ro. Du får tid att tänka igenom beslutet.'
          }, {
            question: 'Vad händer när jag klickar vidare?',
            answer: 'Du kommer till Verisure där du lämnar ditt telefonnummer så att en rådgivare kan ringa upp dig på en tid som passar dig.'
          }, {
            question: 'Hur hanteras mina uppgifter?',
            answer: 'Dina uppgifter lämnas direkt på Verisures sida och hanteras enligt deras integritetspolicy. Vi sparar inga personuppgifter på denna sida.'
          }, {
            question: 'Installerar ni också?',
            answer: 'Ja, Verisure hjälper till med installation, service och övervakning dygnet runt. Allt ingår i paketet för att göra det så enkelt som möjligt för dig.'
          }].map((faq, index) => <div key={index} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-397">
                <button onClick={() => toggleFAQ(index)} className="w-full p-4 text-left font-medium flex justify-between items-center bg-white hover:bg-gray-50 transition-colors" data-id="element-398">
                  {faq.question}
                  {activeFAQ === index ? <ChevronUpIcon className="h-5 w-5 text-red-600" data-id="element-399" /> : <ChevronDownIcon className="h-5 w-5 text-red-600" data-id="element-400" />}
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${activeFAQ === index ? 'max-h-40' : 'max-h-0'}`} data-id="element-401">
                  <div className="p-4 bg-gray-50 text-gray-700" data-id="element-402">
                    {faq.answer}
                  </div>
                </div>
              </div>)}
          </div>
        </div>

        {/* Final CTA Section - Enhanced */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 rounded-lg text-white shadow-lg" data-id="element-403">
          <div className="text-center mb-6" data-id="element-404">
            <h4 className="font-bold text-xl mb-3" data-id="element-405">
              Ta nästa steg mot ett tryggare hem
            </h4>
            <p className="mb-4 text-white/90" data-id="element-406">
              Kostnadsfri rådgivning utan bindning – få ett personligt förslag
            </p>
          </div>
          <div className="bg-white/10 p-4 rounded-lg mb-6" data-id="element-407">
            <div className="flex flex-col space-y-3" data-id="element-408">
              <div className="flex items-center" data-id="element-409">
                <CheckIcon className="h-5 w-5 text-white mr-2" data-id="element-410" />
                <span className="text-white" data-id="element-411">
                  Rådgivare ringer upp på en tid som passar dig
                </span>
              </div>
              <div className="flex items-center" data-id="element-412">
                <CheckIcon className="h-5 w-5 text-white mr-2" data-id="element-413" />
                <span className="text-white" data-id="element-414">
                  Kostnadsfri säkerhetsgenomgång av ditt hem
                </span>
              </div>
              <div className="flex items-center" data-id="element-415">
                <CheckIcon className="h-5 w-5 text-white mr-2" data-id="element-416" />
                <span className="text-white" data-id="element-417">
                  Ingen bindning eller kostnad för rådgivningen
                </span>
              </div>
            </div>
          </div>
          <div className="text-center" data-id="element-418">
            <CTAButton onClick={handleCTAClick} text="Få mer information om säkerhetslösningar" primary={false} size="large" icon={<ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-420" />} href="https://go.adt212.net/t/t?a=1960533651&as=1971362379&t=2&tk=1" className="bg-white text-red-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all" data-id="element-419" />
          </div>
        </div>
      </div>
    </div>;
}